import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, UserPlus } from "lucide-react";

// Parse a stored author string ("Last, First" or "First Last") into {last, first}
export function parseAuthor(str) {
  const a = (str || "").trim();
  if (!a) return { first: "", last: "" };
  if (a.includes(",")) {
    const [last, first = ""] = a.split(",", 2).map((s) => s.trim());
    return { first, last };
  }
  const toks = a.split(/\s+/);
  if (toks.length === 1) return { first: "", last: toks[0] };
  return { first: toks.slice(0, -1).join(" "), last: toks[toks.length - 1] };
}

// Serialise back to "Last, First" (or just Last if no first). Empty pairs are dropped by caller.
export function serialiseAuthor({ first, last }) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  if (!f && !l) return "";
  if (!f) return l;
  if (!l) return f;
  return `${l}, ${f}`;
}

export function authorsToRows(authors = []) {
  const rows = (authors || []).map(parseAuthor);
  return rows.length ? rows : [{ first: "", last: "" }];
}

export function rowsToAuthors(rows) {
  return rows.map(serialiseAuthor).filter(Boolean);
}

export default function AuthorsEditor({ rows, onChange, testIdPrefix = "author" }) {
  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const add = () => onChange([...rows, { first: "", last: "" }]);
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length ? next : [{ first: "", last: "" }]);
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}s-editor`}>
      <Label className="text-xs text-[#7A6A8C]">Authors</Label>
      <div className="space-y-2">
        {rows.map((row, idx) => (
          <div
            key={idx}
            data-testid={`${testIdPrefix}-row-${idx}`}
            className="grid grid-cols-[1fr,1fr,auto] gap-2 items-center"
          >
            <Input
              data-testid={`${testIdPrefix}-first-${idx}`}
              value={row.first}
              onChange={(e) => update(idx, { first: e.target.value })}
              placeholder="First name"
              className="bg-[#FFF5FA] dark:bg-[#3B2350] h-9"
            />
            <Input
              data-testid={`${testIdPrefix}-last-${idx}`}
              value={row.last}
              onChange={(e) => update(idx, { last: e.target.value })}
              placeholder="Last name"
              className="bg-[#FFF5FA] dark:bg-[#3B2350] h-9"
            />
            <button
              type="button"
              data-testid={`${testIdPrefix}-remove-${idx}`}
              onClick={() => remove(idx)}
              disabled={rows.length === 1 && !row.first && !row.last}
              className="w-9 h-9 grid place-items-center rounded-md text-[#7A6A8C] hover:text-[#C9286F] hover:bg-[#FCE4F0] dark:hover:bg-[#4A1D3A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Remove author"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        data-testid={`${testIdPrefix}-add-button`}
        onClick={add}
        className="inline-flex items-center gap-1.5 text-xs text-[#C9286F] dark:text-[#C4A1FF] hover:underline font-medium"
      >
        <UserPlus className="w-3.5 h-3.5" /> Add another author
      </button>
    </div>
  );
}
